import React, { useState, useEffect, useRef } from 'react'
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap'
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from 'react-redux'
import { updateProfile, uploadAvatar } from '../../actions/usersActions'
import AxiosControl from '../../components/axios/AxiosControl'
const Axios = AxiosControl()

export default function Profile() {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { profileUpdating, profileUpdateSuccess, profileUpdateError, avatar, avatarUploading } = useSelector(state => state.users)
    const [form, setForm] = useState({})
    const [loading, setLoading] = useState(true)
    const [preview, setPreview] = useState(null)
    const fileInputRef = useRef(null)

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'instant' })
        fetchProfile()
    }, [])

    const fetchProfile = async () => {
        setLoading(true)
        try {
            const { data, status } = await Axios.get('/api/profile/me', {
                headers: { auth: localStorage.getItem('jwt') }
            })
            if (status === 200 && data.partner) {
                const p = data.partner
                setForm({
                    name: p.name || '',
                    email: p.email || '',
                    phone: p.phone || '',
                    mobile: p.mobile || '',
                    birth_date: p.birth_date || '',
                })
                if (!avatar && data.local?.avatar) {
                    dispatch({ type: 'AVATAR_SUCCESS', payload: data.local.avatar })
                }
            }
        } catch (err) {
            console.error('Error al cargar perfil', err)
        } finally {
            setLoading(false)
        }
    }

    const handleChange = (field) => (e) => {
        setForm(prev => ({ ...prev, [field]: e.target.value }))
    }

    const handleSave = (e) => {
        e.preventDefault()
        dispatch(updateProfile(form))
    }

    const handleAvatarClick = () => {
        fileInputRef.current?.click()
    }

    const handleAvatarChange = async (e) => {
        const file = e.target.files?.[0]
        if (!file) return

        setPreview(URL.createObjectURL(file))

        try {
            const formData = new FormData()
            formData.append('image', file)
            const { data, status } = await Axios.post('/api/upload/uploadimage', formData, {
                headers: {
                    auth: localStorage.getItem('jwt'),
                    'Content-Type': 'multipart/form-data'
                }
            })
            if (status === 200 && typeof data === 'string') {
                dispatch(uploadAvatar(data))
            }
        } catch (err) {
            console.error('Error al subir la foto de perfil', err)
        }
    }

    const loadingSkeleton = <span className='bg-light rounded-3' style={{ display: 'inline-block', height: '20px', width: '120px' }} />

    return (
        <Container className='my-3 p-5'>
            <Row>
                <Col>
                    <Card className='border-0 rounded-4'>
                        <Card.Body className='d-flex align-items-center p-4 gap-4'>
                            <div className='position-relative' style={{ cursor: 'pointer' }} onClick={handleAvatarClick}>
                                <img
                                    src={preview || avatar || '/images/profile.svg'}
                                    alt="avatar"
                                    className='rounded-circle'
                                    style={{ width: '80px', height: '80px', minWidth: '80px', objectFit: 'cover', border: '3px solid #dc2626', background: '#f3f4f6' }}
                                />
                                <div className='position-absolute bottom-0 end-0 d-flex align-items-center justify-content-center rounded-circle bg-danger text-white'
                                    style={{ width: '28px', height: '28px', border: '2px solid #fff' }}>
                                    {avatarUploading ? (
                                        <span className="spinner-border spinner-border-sm" style={{ fontSize: '10px' }}></span>
                                    ) : (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                                            <circle cx="12" cy="13" r="4" />
                                        </svg>
                                    )}
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleAvatarChange}
                                    style={{ display: 'none' }}
                                />
                            </div>
                            <div className='flex-grow-1'>
                                <h3 className='fw-bold text-black mb-1'>{loading ? loadingSkeleton : (form.name || '—')}</h3>
                                <p className='text-muted mb-1'>{loading ? loadingSkeleton : (form.email || '—')}</p>
                                <div className='d-flex gap-3'>
                                    <span className='badge bg-danger bg-opacity-10 text-danger px-3 py-2 rounded-pill'>
                                        {t('profile.role')}
                                    </span>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className='mt-4'>
                <Col>
                    <Card className='border-0 rounded-4'>
                        <Card.Body className='p-4'>
                            {profileUpdateSuccess && (
                                <Alert variant='success' dismissible onClose={() => dispatch({ type: 'PROFILE_UPDATE_SUCCESS', payload: null })}>
                                    {profileUpdateSuccess}
                                </Alert>
                            )}
                            {profileUpdateError && (
                                <Alert variant='danger' dismissible onClose={() => dispatch({ type: 'PROFILE_UPDATE_ERROR', payload: null })}>
                                    {profileUpdateError}
                                </Alert>
                            )}

                            <h5 className='fw-bold text-black mb-1'>{t('profile.information')}</h5>
                            <p className='text-muted mb-4'>{t('profile.subtitle')}</p>
                            <Form onSubmit={handleSave}>
                                <Row className='g-4'>
                                    <Col xs={12} md={6}>
                                        <Form.Group>
                                            <Form.Label className='text-muted fw-semibold'>{t('profile.fullname')}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                value={form.name || ''}
                                                onChange={handleChange('name')}
                                                className='sd-input'
                                                disabled={loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={6}>
                                        <Form.Group>
                                            <Form.Label className='text-muted fw-semibold'>{t('profile.email')}</Form.Label>
                                            <Form.Control
                                                type="email"
                                                value={form.email || ''}
                                                onChange={handleChange('email')}
                                                placeholder="—"
                                                className='sd-input'
                                                disabled={loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={6}>
                                        <Form.Group>
                                            <Form.Label className='text-muted fw-semibold'>{t('profile.phone')}</Form.Label>
                                            <Form.Control
                                                type="tel"
                                                value={form.phone || ''}
                                                onChange={handleChange('phone')}
                                                className='sd-input'
                                                disabled={loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col xs={12} md={6}>
                                        <Form.Group>
                                            <Form.Label className='text-muted fw-semibold'>{t('profile.birthdate')}</Form.Label>
                                            <Form.Control
                                                type="date"
                                                value={form.birth_date || ''}
                                                onChange={handleChange('birth_date')}
                                                className='sd-input'
                                                disabled={loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className='d-flex justify-content-end mt-4'>
                                    <Button type="submit" className='btn-primary px-5' disabled={loading || profileUpdating}>
                                        {profileUpdating ? (
                                            <span className="spinner-border spinner-border-sm align-middle"></span>
                                        ) : (
                                            t('profile.save')
                                        )}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    )
}
